import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import styled from 'styled-components';
import { fetchNotices, Notice } from '@/api/notice';

import { formatDateDay } from '@/utils/dateUtils';
import { Pagination } from 'antd';

const LatestNews = () => {
  const navigate = useNavigate();
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const initialPage = parseInt(params.get('page') || '1', 10);
  const filter = params.get('filter') || 'all';
  const keyword = (params.get('keyword') || '').toLowerCase().trim();
  const [page, setPage] = useState(initialPage);
  const pageSize = 10;

  const { data, isLoading, isError } = useQuery<Notice[]>({
    queryKey: ['notices'],
    queryFn: fetchNotices,
  });

  if (isLoading) return <StatusText>불러오는 중...</StatusText>;
  if (isError || !data) return <StatusText>데이터 로딩 실패</StatusText>;

  // ✅ 필터링

  const filtered = data.filter((item) => {
    const title = item.title.toLowerCase();
    const content = item.content?.toLowerCase() || '';
    if (!keyword) return true;

    if (filter === 'title') return content.includes(keyword);
    return title.includes(keyword) || content.includes(keyword);
  });

  const totalPages = Math.ceil(filtered.length / pageSize);
  const totalItems = filtered.length;
  const reversed = [...filtered];
  const pagedNotices = reversed.slice((page - 1) * pageSize, page * pageSize);

  return (
    <>
      <Container>
        {pagedNotices.map((notice) => {
          const formattedDate = formatDateDay(notice.openedAt); // ✅ 날짜 포맷 적용

          return (
            <NewsCard key={notice.id} onClick={() => navigate(`/intro/news/${notice.id}`)}>
              <LeftBox>
                <NewsIndex>{notice.sortIndex}</NewsIndex>
              </LeftBox>
              <ContentBox>
                <NewsTitle>{notice.title}</NewsTitle>
                <NewsDate>{formattedDate}</NewsDate>
              </ContentBox>
            </NewsCard>
          );
        })}
      </Container>
      <PaginationWrapper>
        {totalPages > 1 && (
          <Pagination
            current={page}
            pageSize={pageSize}
            total={totalItems}
            onChange={(newPage) => setPage(newPage)}
            showSizeChanger={false} // 페이지 크기 선택 비활성화
          />
        )}
      </PaginationWrapper>
    </>
  );
};

export default LatestNews;

// ✅ styled-components

const Container = styled.div`
  display: flex;
  width: 100%;
  max-width: 850px;
  flex-direction: column;
  align-items: flex-start;
  padding: 0 16px;
  box-sizing: border-box;
`;

const NewsCard = styled.div`
  display: flex;
  padding: 16px 12px;
  align-items: center;
  gap: 10px;
  align-self: stretch;
  flex-wrap: wrap;
  cursor: pointer;

  @media (max-width: 768px) {
    align-items: flex-start;
    gap: 8px;
  }

  &:hover {
    background-color: #f0f3fe;
    color: #0b46dd;
  }
`;

const LeftBox = styled.div`
  display: flex;
  width: 56px;
  padding-right: 16px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 10px;

  @media (max-width: 768px) {
    padding-right: 0;
    width: auto;
  }
`;

const NewsIndex = styled.div`
  color: var(--gray-ui_gray05, #999);
  font-size: 18px;
  font-weight: 700;
  line-height: 120%;
  letter-spacing: -0.36px;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const ContentBox = styled.div`
  flex: 1 0 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  text-align: start;
  width: 100%;

  @media (max-width: 768px) {
    align-items: flex-start;
    gap: 4px;
  }
`;

const NewsTitle = styled.div`
  flex: 1;
  color: var(--gray-ui_gray03, #3a3a3a);
  font-size: 18px;
  font-weight: 400;
  line-height: 120%;
  letter-spacing: -0.36px;

  @media (max-width: 768px) {
    white-space: normal;
    font-size: 16px;
  }
`;

const NewsDate = styled.div`
  color: var(--gray-ui_gray04, #6f6f6f);
  text-align: right;
  font-size: 14px;
  font-weight: 400;
  line-height: 120%;
  letter-spacing: -0.28px;
  margin-left: 12px;

  @media (max-width: 768px) {
    margin-left: 0;
    font-size: 13px;
  }
`;

const StatusText = styled.div`
  width: 100%;
  text-align: center;
  padding: 32px 0;
  font-size: 16px;
  color: #999;

  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const PaginationWrapper = styled.div`
  margin-top: 40px; // ✅ 여백 고정
  margin-bottom: 40px; // ✅ 여백 고정
  min-height: 40px; // ✅ 빈 공간 확보 (페이지네이션 없을 때도)
  display: flex;
  justify-content: center;
`;
